import {
  Box,
  HStack,
  Heading,
  IconButton,
  Stack,
  Text,
  Link,
  useBreakpointValue,
} from "@chakra-ui/react";
import { EventDetailDescriptionSection } from "@common/interfaces/KYDEvent";
import { FiEdit2 } from "react-icons/fi";
import Markdown from "react-markdown";
import { SectionProps } from "../sections/sectionsComponents";
import { EditModalType, markdownToLineBreaks } from "../../../utils";
import EditDescriptionModal from "./EditDescriptionModal";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import { ScheduledUpdatesBadge } from "../Lineup/ScheduledUpdatesBadge";
import useScheduledUpdates from "../ScheduledUpdates/hooks/useScheduledUpdates";
import UpcomingScheduledUpdateDialog, {
  UpcomingScheduledUpdateDialogContext,
  withUpcomingScheduledUpdateDialog,
} from "../ScheduledUpdates/UpcomingScheduledUpdateDialog";
import { useContext } from "react";
import {
  ScheduledUpdatesContext,
  withScheduledUpdates,
} from "../ScheduledUpdates/ScheduledUpdatesContext";
import EventDetailTitle from "../../EventDetailTitle";
import BodyDocRenderer from "./BodyDocRenderer";

const EventDescription = ({
  data,
  isLoading,
  setModalType,
  onUpdateEvent,
  permissions,
  modalType,
}: SectionProps<EventDetailDescriptionSection["data"]>) => {
  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });
  const internalDescription = markdownToLineBreaks(data?.description);
  const {
    state: { hasScheduledUpdatesForField, getScheduledUpdateForField },
  } = useScheduledUpdates();
  const { open: openScheduledUpdates } = useContext(ScheduledUpdatesContext);
  const {
    isOpen: isUpcomingScheduledUpdateDialogOpen,
    open: openUpcomingScheduledUpdateDialog,
    close: closeUpcomingScheduledUpdateDialog,
  } = useContext(UpcomingScheduledUpdateDialogContext);

  return (
    <Stack spacing={2}>
      <UpcomingScheduledUpdateDialog
        isLoading={isLoading}
        onEditScheduledUpdate={() => {
          console.log("onEditScheduledUpdate");
          const scheduledUpdate = getScheduledUpdateForField(
            data.scheduled_updates,
            "description"
          );
          console.log("scheduledUpdate", scheduledUpdate);

          closeUpcomingScheduledUpdateDialog();
          if (scheduledUpdate) {
            openScheduledUpdates({ scheduledUpdate, field: "description" });
          }
        }}
      />
      <EditDescriptionModal
        isOpen={modalType === EditModalType.DESCRIPTION}
        isLoading={isLoading}
        onSave={async (values) => {
          await onUpdateEvent("description_rich", values.description_rich);

          if (
            hasScheduledUpdatesForField(data.scheduled_updates, "description")
          ) {
            openUpcomingScheduledUpdateDialog();
          }
        }}
        setModalType={setModalType}
        data={data}
      />
      <HStack justifyContent={"space-between"}>
        <Stack direction={["column", "row"]} alignItems={"center"} gap={2}>
          <EventDetailTitle>Description</EventDetailTitle>
          <ScheduledUpdatesBadge
            scheduledUpdates={data.scheduled_updates}
            field="description"
            hasScheduledUpdatesForField={hasScheduledUpdatesForField}
          />
        </Stack>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          <IconButton
            isDisabled={isLoading}
            onClick={() => setModalType(EditModalType.DESCRIPTION)}
            icon={<FiEdit2 fontSize="1.25rem" />}
            variant="ghost"
            aria-label="Edit member"
          />
        </ShowIfHasPermission>
      </HStack>
      {data?.description || data?.description_rich ? (
        <Box
          borderWidth={"1px"}
          borderColor="gray.200"
          borderRadius={"lg"}
          p={4}
        >
          {data?.description_rich ? (
            <BodyDocRenderer
              content={data.description_rich}
              isMobile={isMobile}
            />
          ) : internalDescription ? (
            <Markdown
              //@ts-ignore
              className="mdx-editor"
              components={{
                a: (props) => (
                  <Link
                    href={props.href}
                    target={"_blank"}
                    textDecoration={"underline"}
                    color={isMobile ? "blue.200" : "blue.800"}
                  >
                    {props.children}
                  </Link>
                ),
                h1: (props) => <Heading {...props} fontSize={24} />,
                h2: (props) => <Heading {...props} fontSize={22} />,
                h3: (props) => <Heading {...props} fontSize={20} />,
                h4: (props) => <Heading {...props} fontSize={18} />,
                h5: (props) => <Heading {...props} fontSize={16} />,
                p: (props) => <Text {...props}>{props.children}</Text>,
              }}
            >
              {internalDescription}
            </Markdown>
          ) : null}
        </Box>
      ) : (
        <Text color="gray.500">No description added yet</Text>
      )}
    </Stack>
  );
};

export default withScheduledUpdates(
  withUpcomingScheduledUpdateDialog(EventDescription)
);
